import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Location } from "../../Interfaces"
import { locationService } from "../../services/locationService"
import { useSiteTerminology } from "../../contexts/PublicSiteContext"
import UiContext from "../../context/uiProvider"
import { useContext } from "react"
import { TABLET } from "../../utils/screenSize"

export function hasBadGeocode(latitude?: number | null, longitude?: number | null): boolean {
  const lat = Number(latitude)
  const lng = Number(longitude)
  return !Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)
}

interface LocationTitleCardProps {
  location: Location
  organizationName?: string
  managementCompanyName?: string
  onLocationUpdated?: (location: Location) => void
}

export default function LocationTitleCard({
  location,
  organizationName,
  managementCompanyName,
  onLocationUpdated,
}: LocationTitleCardProps) {
  const { deviceView } = useContext(UiContext)
  const { managementCompanySingular, areaSingular } = useSiteTerminology()
  const queryClient = useQueryClient()
  const [isGeocoding, setIsGeocoding] = useState(false)
  const showRegeocode = hasBadGeocode(location.latitude, location.longitude)

  const handleRegeocode = async () => {
    if (!location.id || isGeocoding) return
    setIsGeocoding(true)
    try {
      const updated = await locationService.regeocode(location.id)
      await queryClient.invalidateQueries(["location", location.id])
      onLocationUpdated?.(updated)
      alert("Location coordinates updated from the address.")
    } catch (error: any) {
      alert(
        error?.message ||
          "The address appears to be invalid. Please check it and try again.",
      )
    } finally {
      setIsGeocoding(false)
    }
  }

  return (
    <section className="bg-secondary-extra-light p-2 md:rounded-lg mb-2 shadow-lg">
      <div className="bg-primary-dark flex flex-col">
        <div className="text-white rounded-lg p-4">
          <div className={`${deviceView > TABLET && "flex"}`}>
            <div className="mr-5">
              <h3 className="text-white">{location.name}</h3>
              <h4 className="text-white">{location.address}</h4>
              <h4 className="text-white">{`${location.city}, ${location.state} ${location.zipcode}`}</h4>
              {showRegeocode && (
                <div className="mt-3">
                  <p className="text-sm text-amber-200 mb-2">
                    Map location is missing or invalid for this address.
                  </p>
                  <button
                    type="button"
                    onClick={handleRegeocode}
                    disabled={isGeocoding}
                    className="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-primary hover:bg-gray-100 disabled:opacity-60"
                  >
                    {isGeocoding ? "Geocoding…" : "Re-run Geocode"}
                  </button>
                </div>
              )}
            </div>
            <div>
              {managementCompanyName && (
                <h4 className="text-white">
                  {managementCompanySingular}:
                  <span className="text-base">{" " + managementCompanyName}</span>
                </h4>
              )}
              <h4 className="text-white">
                {areaSingular}:
                <span className="text-base">{" " + organizationName}</span>
              </h4>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
